import React from 'react';
import { Skeleton, SkeletonText, SkeletonRegion } from '../ui/Skeleton';

/**
 * Page-shaped loading placeholders. Each mirrors the layout of the page it stands in for,
 * so content swaps in without the page jumping. They only show on a page's first load;
 * after that, data comes from the TanStack Query cache.
 */

const range = (n: number) => Array.from({ length: n }, (_, i) => i);

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

/** Matches ProjectCard: 420px card, 12rem image, title, text, tags, button. */
export const ProjectCardSkeleton: React.FC = () => (
  <div className="card h-[420px] flex flex-col">
    <Skeleton className="h-48 w-full rounded-none flex-shrink-0" />
    <div className="p-6 flex flex-col flex-grow">
      <Skeleton className="h-6 w-3/4 mb-4" />
      <SkeletonText lines={3} lineClassName="h-3.5" className="mb-5" />
      <div className="flex gap-2 mb-4">
        <Skeleton className="h-6 w-16" />
        <Skeleton className="h-6 w-20" />
        <Skeleton className="h-6 w-14" />
      </div>
      <Skeleton className="h-9 w-full mt-auto" />
    </div>
  </div>
);

export const ProjectGridSkeleton: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <SkeletonRegion label="Loading projects…" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
    {range(count).map((i) => (
      <ProjectCardSkeleton key={i} />
    ))}
  </SkeletonRegion>
);

export const FilterChipsSkeleton: React.FC<{ count?: number }> = ({ count = 5 }) => (
  <>
    {range(count).map((i) => (
      <Skeleton key={i} className="h-9 rounded-md" style={{ width: `${72 + ((i * 23) % 48)}px` }} />
    ))}
  </>
);

/** Generic content card used by the open-source style pages. */
const InfoCardSkeleton: React.FC = () => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 flex flex-col">
    <div className="flex items-center gap-3 mb-4">
      <Skeleton className="h-10 w-10 rounded-lg" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-3 w-1/3" />
      </div>
    </div>
    <SkeletonText lines={3} lineClassName="h-3.5" className="mb-5" />
    <div className="flex gap-2 mb-5">
      <Skeleton className="h-6 w-16 rounded-full" />
      <Skeleton className="h-6 w-20 rounded-full" />
    </div>
    <div className="flex justify-between mt-auto">
      <Skeleton className="h-4 w-20" />
      <Skeleton className="h-4 w-16" />
    </div>
  </div>
);

const SectionHeadingSkeleton: React.FC<{ centered?: boolean }> = ({ centered }) => (
  <div className={`mb-10 ${centered ? 'flex flex-col items-center' : ''}`}>
    <Skeleton className="h-9 w-64 max-w-full mb-4" />
    <Skeleton className="h-5 w-96 max-w-full" />
  </div>
);

// ---------------------------------------------------------------------------
// Home
// ---------------------------------------------------------------------------

export const HomePageSkeleton: React.FC = () => (
  <SkeletonRegion label="Loading home page…">
    <section className="py-24 lg:pt-[var(--page-top)] bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-primary-900/30">
      <div className="container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 lg:pr-8">
            <div className="flex items-center mb-6">
              <Skeleton className="h-1 w-12 mr-4" />
              <Skeleton className="h-4 w-40" />
            </div>
            <Skeleton className="h-14 md:h-16 w-11/12 mb-3" />
            <Skeleton className="h-14 md:h-16 w-2/3 mb-8" />
            <Skeleton className="h-8 w-3/4 mb-6" />
            <SkeletonText lines={4} lineClassName="h-4" className="max-w-2xl mb-8" />
            <div className="flex flex-wrap gap-4 mb-8">
              <Skeleton className="h-11 w-36" />
              <Skeleton className="h-11 w-40" />
            </div>
            <div className="flex items-center gap-3 mb-6">
              <Skeleton className="h-4 w-20 mr-2" />
              {range(4).map((i) => (
                <Skeleton key={i} className="h-10 w-10 rounded-full" />
              ))}
            </div>
            <Skeleton className="h-4 w-44 mb-3" />
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
              {range(8).map((i) => (
                <Skeleton key={i} className="h-10 rounded-lg" />
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 flex justify-center">
            <Skeleton className="w-64 h-64 md:w-80 md:h-80 rounded-full" />
          </div>
        </div>
      </div>
    </section>

    <section className="py-20 bg-white dark:bg-gray-900">
      <div className="container">
        <SectionHeadingSkeleton />
        <ProjectGridSkeleton count={3} />
      </div>
    </section>
  </SkeletonRegion>
);

// ---------------------------------------------------------------------------
// Project detail
// ---------------------------------------------------------------------------

export const ProjectDetailSkeleton: React.FC = () => (
  <SkeletonRegion label="Loading project…" className="container py-16 lg:pt-[var(--page-top)]">
    <div className="mb-12">
      <Skeleton className="h-10 w-2/3 mb-6" />
      <SkeletonText lines={2} lineClassName="h-5" className="max-w-3xl mb-5" />
      <div className="flex flex-wrap gap-2">
        {range(4).map((i) => (
          <Skeleton key={i} className="h-7 w-20" />
        ))}
      </div>
    </div>
    <Skeleton className="w-full aspect-video rounded-lg mb-12" />
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2">
        <Skeleton className="h-7 w-56 mb-5" />
        <SkeletonText lines={6} className="mb-8" />
        <Skeleton className="h-7 w-40 mb-5" />
        <SkeletonText lines={4} />
      </div>
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md h-fit">
        <Skeleton className="h-6 w-32 mb-5" />
        {range(4).map((i) => (
          <div key={i} className="mb-4">
            <Skeleton className="h-3 w-20 mb-2" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ))}
        <Skeleton className="h-10 w-full mt-2" />
      </div>
    </div>
  </SkeletonRegion>
);

// ---------------------------------------------------------------------------
// Resume
// ---------------------------------------------------------------------------

const TimelineCardSkeleton: React.FC = () => (
  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
    <div className="flex flex-col md:flex-row md:justify-between gap-2 mb-4">
      <div className="space-y-2 flex-1">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-4 w-1/3" />
      </div>
      <Skeleton className="h-4 w-32" />
    </div>
    <SkeletonText lines={3} lineClassName="h-3.5" />
  </div>
);

export const ResumePageSkeleton: React.FC = () => (
  <SkeletonRegion label="Loading resume…" className="container py-16 lg:pt-[var(--page-top)]">
    <Skeleton className="h-10 w-40 mb-6" />
    <div className="flex justify-end mb-6">
      <Skeleton className="h-5 w-32" />
    </div>

    <div className="mb-12">
      <Skeleton className="h-7 w-60 mb-4" />
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
        <SkeletonText lines={4} />
      </div>
    </div>

    <div className="mb-12">
      <Skeleton className="h-7 w-72 mb-6" />
      <div className="space-y-6">
        {range(3).map((i) => (
          <TimelineCardSkeleton key={i} />
        ))}
      </div>
    </div>

    <div className="mb-12">
      <Skeleton className="h-7 w-44 mb-6" />
      <div className="space-y-6">
        {range(2).map((i) => (
          <TimelineCardSkeleton key={i} />
        ))}
      </div>
    </div>

    <div>
      <Skeleton className="h-7 w-52 mb-6" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {range(4).map((i) => (
          <div key={i} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <Skeleton className="h-5 w-1/2 mb-4" />
            <div className="flex flex-wrap gap-2">
              {range(5).map((j) => (
                <Skeleton key={j} className="h-7 w-20 rounded-full" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </SkeletonRegion>
);

// ---------------------------------------------------------------------------
// Publications
// ---------------------------------------------------------------------------

export const PublicationsPageSkeleton: React.FC = () => (
  <SkeletonRegion label="Loading publications…" className="container py-16 lg:pt-[var(--page-top)]">
    <Skeleton className="h-10 w-52 mb-6" />
    <div className="relative mb-16 rounded-3xl bg-white/80 dark:bg-gray-800/80 shadow-xl p-8 md:p-12">
      <Skeleton className="h-8 w-72 max-w-full mb-6" />
      <SkeletonText lines={3} className="max-w-4xl mb-10" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        {range(4).map((i) => (
          <div key={i} className="flex flex-col items-center gap-2">
            <Skeleton className="h-10 w-16" />
            <Skeleton className="h-4 w-24" />
          </div>
        ))}
      </div>
    </div>

    <Skeleton className="h-8 w-60 mb-6" />
    <div className="space-y-6">
      {range(4).map((i) => (
        <div key={i} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
          <Skeleton className="h-6 w-4/5 mb-3" />
          <Skeleton className="h-4 w-1/2 mb-4" />
          <SkeletonText lines={2} lineClassName="h-3.5" className="mb-4" />
          <div className="flex gap-2">
            <Skeleton className="h-6 w-16 rounded-full" />
            <Skeleton className="h-6 w-24 rounded-full" />
            <Skeleton className="h-6 w-20 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  </SkeletonRegion>
);

// ---------------------------------------------------------------------------
// Open source hub
// ---------------------------------------------------------------------------

export const OpenSourcePageSkeleton: React.FC = () => (
  <SkeletonRegion
    label="Loading open source work…"
    className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 dark:from-gray-900 dark:via-indigo-900 dark:to-blue-900"
  >
    <section className="py-20 lg:pt-[var(--page-top)]">
      <div className="container flex flex-col items-center text-center mb-4">
        <Skeleton className="h-4 w-44 mb-6" />
        <Skeleton className="h-12 md:h-16 w-[42rem] max-w-full mb-6" />
        <Skeleton className="h-6 w-[36rem] max-w-full mb-3" />
        <Skeleton className="h-6 w-[28rem] max-w-full" />
        <div className="flex flex-wrap justify-center gap-8 mt-12">
          {range(4).map((i) => (
            <div key={i} className="flex flex-col items-center gap-2">
              <Skeleton className="h-9 w-14" />
              <Skeleton className="h-4 w-28" />
            </div>
          ))}
        </div>
      </div>
    </section>
    <section className="pb-20">
      <div className="container space-y-16">
        {range(2).map((s) => (
          <div key={s}>
            <SectionHeadingSkeleton />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {range(3).map((i) => (
                <InfoCardSkeleton key={i} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  </SkeletonRegion>
);

// ---------------------------------------------------------------------------
// Pub packages / starter templates (gradient hero + card grid)
// ---------------------------------------------------------------------------

const GradientHeroPageSkeleton: React.FC<{
  label: string;
  pageClassName: string;
  heroClassName: string;
}> = ({ label, pageClassName, heroClassName }) => (
  <SkeletonRegion label={label} className={`min-h-screen ${pageClassName}`}>
    <div className="relative overflow-hidden">
      <div className={`absolute inset-0 opacity-90 ${heroClassName}`} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:pt-[var(--page-top)] flex flex-col items-center text-center">
        <Skeleton onColor className="h-12 md:h-16 w-[36rem] max-w-full mb-6" />
        <Skeleton onColor className="h-6 w-[40rem] max-w-full mb-3" />
        <Skeleton onColor className="h-6 w-[30rem] max-w-full mb-8" />
        <div className="flex flex-wrap justify-center gap-6">
          {range(4).map((i) => (
            <Skeleton key={i} onColor className="h-7 w-32" />
          ))}
        </div>
      </div>
    </div>
    <section className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeadingSkeleton centered />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {range(6).map((i) => (
            <InfoCardSkeleton key={i} />
          ))}
        </div>
      </div>
    </section>
  </SkeletonRegion>
);

export const PubPackagesPageSkeleton: React.FC = () => (
  <GradientHeroPageSkeleton
    label="Loading packages…"
    pageClassName="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900"
    heroClassName="bg-gradient-to-r from-blue-600 to-purple-600"
  />
);

export const StarterTemplatesPageSkeleton: React.FC = () => (
  <GradientHeroPageSkeleton
    label="Loading developer tools…"
    pageClassName="bg-gradient-to-br from-purple-50 via-pink-50 to-red-50 dark:from-gray-900 dark:via-purple-900 dark:to-indigo-900"
    heroClassName="bg-gradient-to-r from-purple-600 to-pink-600"
  />
);

// ---------------------------------------------------------------------------
// Contact
// ---------------------------------------------------------------------------

export const ContactPageSkeleton: React.FC = () => (
  <SkeletonRegion label="Loading contact details…" className="container py-16 lg:pt-[var(--page-top)]">
    <Skeleton className="h-10 w-48 mb-6" />
    <div className="flex flex-col items-center mb-12">
      <Skeleton className="h-4 w-[40rem] max-w-full mb-2.5" />
      <Skeleton className="h-4 w-[30rem] max-w-full" />
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
      <div>
        <Skeleton className="h-7 w-40 mb-6" />
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-8 space-y-6">
          {range(3).map((i) => (
            <div key={i} className="flex items-start">
              <Skeleton className="h-12 w-12 rounded-full mr-4 flex-shrink-0" />
              <div className="flex-1 space-y-2 pt-1">
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-4 w-48 max-w-full" />
              </div>
            </div>
          ))}
        </div>
        <Skeleton className="h-7 w-36 mb-4" />
        <div className="flex gap-3">
          {range(4).map((i) => (
            <Skeleton key={i} className="h-11 w-11 rounded-full" />
          ))}
        </div>
      </div>
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
        <Skeleton className="h-7 w-48 mb-6" />
        {range(3).map((i) => (
          <div key={i} className="mb-5">
            <Skeleton className="h-4 w-20 mb-2" />
            <Skeleton className="h-10 w-full" />
          </div>
        ))}
        <Skeleton className="h-4 w-20 mb-2" />
        <Skeleton className="h-32 w-full mb-6" />
        <Skeleton className="h-11 w-36" />
      </div>
    </div>
  </SkeletonRegion>
);

// ---------------------------------------------------------------------------
// Writing hub / course / lesson / article — reuse the course stylesheet's layout classes
// ---------------------------------------------------------------------------

export const WritingHubSkeleton: React.FC = () => (
  <SkeletonRegion label="Loading writing…" className="course-section writing-hub__section writing-hub__wrap">
    <div className="writing-hub__browse">
      <div className="writing-hub__toolbar">
        <Skeleton className="h-10 flex-1 min-w-[12rem] rounded-lg" />
        <div className="flex gap-2">
          {range(3).map((i) => (
            <Skeleton key={i} className="h-9 w-24 rounded-lg" />
          ))}
        </div>
      </div>
      <div className="writing-hub__filters">
        <Skeleton className="h-4 w-16 mb-5" />
        {range(4).map((g) => (
          <div key={g} className="mb-5">
            <Skeleton className="h-4 w-28 mb-3" />
            {range(3).map((i) => (
              <Skeleton key={i} className="h-3.5 mb-2.5" style={{ width: `${55 + ((g + i) * 13) % 35}%` }} />
            ))}
          </div>
        ))}
      </div>
      <div className="writing-hub__browse-main">
        <div className="writing-hub__grid">
          {range(6).map((i) => (
            <div key={i} className="writing-hub__card">
              <Skeleton className="w-full aspect-video rounded-none" />
              <div className="writing-hub__card-body">
                <Skeleton className="h-3 w-16 mb-3" />
                <Skeleton className="h-6 w-4/5 mb-3" />
                <SkeletonText lines={2} lineClassName="h-3.5" className="mb-4" />
                <Skeleton className="h-3 w-24 mb-5" />
                <Skeleton className="h-9 w-32 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </SkeletonRegion>
);

export const CoursePageSkeleton: React.FC = () => (
  <SkeletonRegion label="Loading curriculum…" className="course-shell">
    <section className="course-hero course-hero--compact">
      <div className="course-wrap">
        <Skeleton className="h-4 w-28 mb-8" />
        <Skeleton className="h-4 w-48 mb-4" />
        <Skeleton className="h-10 w-3/4 mb-3" />
        <Skeleton className="h-10 w-1/2 mb-6" />
        <SkeletonText lines={2} className="max-w-2xl mb-8" />
        <div className="flex flex-wrap items-center gap-4">
          <Skeleton className="h-12 w-44 rounded-full" />
          <Skeleton className="h-4 w-36" />
        </div>
      </div>
    </section>
    <main>
      <section className="course-section course-wrap">
        {range(3).map((c) => (
          <div key={c} className="course-chapter-group">
            <div className="course-chapter-group__heading">
              <Skeleton className="h-12 w-12 rounded-lg flex-shrink-0" />
              <div className="course-chapter-group__intro flex-1">
                <Skeleton className="h-3 w-20 mb-3" />
                <Skeleton className="h-7 w-2/3 mb-3" />
                <Skeleton className="h-4 w-4/5 mb-4" />
                <div className="flex gap-3">
                  <Skeleton className="h-3.5 w-14" />
                  <Skeleton className="h-3.5 w-20" />
                  <Skeleton className="h-3.5 w-16" />
                </div>
              </div>
            </div>
            <div className="course-module-list">
              {range(3).map((m) => (
                <div key={m} className="flex items-center gap-4 py-4">
                  <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
                  <div className="flex-1">
                    <Skeleton className="h-3 w-20 mb-2" />
                    <Skeleton className="h-5" style={{ width: `${50 + ((c + m) * 11) % 35}%` }} />
                  </div>
                  <Skeleton className="h-4 w-16" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>
    </main>
  </SkeletonRegion>
);

/** Shared by course lessons and standalone articles (same reading layout). */
export const LessonPageSkeleton: React.FC<{ label?: string; withMeta?: boolean }> = ({
  label = 'Loading lesson…',
  withMeta = true,
}) => (
  <SkeletonRegion label={label} className="course-shell lesson-shell">
    <header className="lesson-header">
      <div className="course-wrap lesson-header__inner">
        <Skeleton className="h-4 w-32 mb-6" />
        <div className="flex items-start gap-4 mb-4">
          {withMeta && <Skeleton className="h-12 w-12 rounded-lg flex-shrink-0" />}
          <div className="flex-1">
            <Skeleton className="h-4 w-56 max-w-full mb-3" />
            <Skeleton className="h-10 w-3/4" />
          </div>
        </div>
        <Skeleton className="h-5 w-2/3 mb-5" />
        {withMeta && (
          <div className="flex gap-8">
            {range(3).map((i) => (
              <div key={i}>
                <Skeleton className="h-3 w-20 mb-2" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        )}
      </div>
    </header>
    <div className="course-wrap lesson-layout">
      <aside className="lesson-aside">
        <Skeleton className="h-3 w-20 mb-4" />
        {range(7).map((i) => (
          <Skeleton key={i} className="h-3.5 mb-3" style={{ width: `${60 + (i * 17) % 35}%` }} />
        ))}
      </aside>
      <div>
        {range(3).map((i) => (
          <div key={i} className="mb-10">
            <Skeleton className="h-7 w-1/2 mb-5" />
            <SkeletonText lines={5} lineClassName="h-5" />
          </div>
        ))}
      </div>
    </div>
  </SkeletonRegion>
);
